import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { parseDesignationText } from 'components/WorkspaceEditor/features/drawingDesignation/services/textParser'
import React, { memo, useMemo } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { ContentEditorProps } from '../../ContentEditor'
import { elementToEditorData } from '../../ContentEditor/contentUtils'
import { withLazyLoad } from '../../withLazyComponent'
import { KeyedText } from './KeyedText'

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/** Props for designation field (for Product Type nodes) */
type DesignationProps = {
    /** Whether this is a Product Type node (for drawing designation field) */
    isProductType?: boolean
    /** Content item ID for drawing designation */
    contentItemId?: string
    /** Node ID for drawing designation */
    nodeId?: string
    /** Whether this key is a PT key (from template) */
    isPTKey?: boolean
}

/** Props for the text editor component */
interface EditorContentProps extends ContentEditorProps {
    /** Whether this text has a label prefix (like "Title:") */
    isKeyedText: boolean
    /** The label text that appears before the editable portion */
    keyedText: string
    /** The editable text portion after the label */
    keyedTextValue?: string
    /** CSS styling classes */
    classes?: string
    /** Designation field props (for Product Type nodes) */
    designationProps?: DesignationProps
}

/**
 * A text editor that handles two display formats:
 * 1. Regular text editing (standard mode)
 * 2. Label-value pairs where the label (with semicolon) is fixed and only the value is editable
 *
 * Content team members always see the standard editor regardless of format.
 *
 * @param {EditorContentProps} props - Props for the component.
 */
export const EditorContent = memo<EditorContentProps>(
    ({
        initialData,
        initialSuggestions,
        clickCoordinates,
        handlers,
        isKeyedText,
        keyedText,
        keyedTextValue,
        classes,
        designationProps,
    }) => {
        const isContentTeamFromStudioPage = useShallowSelector(
            (state) => state.isContentTeamFromStudioPage
        )
        const { previewFeatures } = usePreviewFeatures()
        const { isProductType, contentItemId, nodeId, isPTKey } = designationProps || {}

        // Lock key editing if it's a PT key OR if feature flag is disabled for keyed text
        const shouldLockKey = isPTKey || (isKeyedText && !previewFeatures.hasKeyModification)

        // For Product Types, parse keyedText
        const textPrefix = useMemo(() => {
            if (isProductType && previewFeatures.hasDrawingDesignation) {
                return parseDesignationText(keyedText).prefix
            }
            return keyedText
        }, [keyedText, isProductType, previewFeatures.hasDrawingDesignation])

        // For locked keys (PT keys or feature flag disabled), extract only the value portion for editing
        // Content team members always see full text regardless of lock state
        const editorInitialData = useMemo(() => {
            let result
            if (isKeyedText && shouldLockKey && keyedTextValue !== undefined && !isContentTeamFromStudioPage) {
                // Locked keys (non-content team): only edit the value portion
                // Check if keyedTextValue is empty/whitespace - if so, use empty paragraph
                const trimmedValue = keyedTextValue.trim()
                if (!trimmedValue) {
                    result = '<p></p>'
                } else {
                    // Create a proper element object with id and type for elementToEditorData
                    result = elementToEditorData({
                        id: contentItemId || 'temp-id',
                        text: keyedTextValue,
                        type: 'Text'
                    })
                }
            } else {
                // For all other cases (content team, unlocked keyed text, regular text), use full initialData
                result = initialData
            }

            return result
        }, [isKeyedText, shouldLockKey, keyedTextValue, initialData, contentItemId, isContentTeamFromStudioPage])

        const editorComponent = (
            <ContentEditor
                initialData={editorInitialData}
                initialSuggestions={initialSuggestions}
                clickCoordinates={clickCoordinates}
                handlers={{
                    onReady: handlers?.onReady,
                    onBlur: handlers?.onBlur,
                    onChange: handlers?.onChange,
                }}
            />
        )

        // Check if this is a Product Type with designation format
        // Only render designation for PT keys (first element) to avoid duplication
        const hasDesignationMode =
            previewFeatures.hasDrawingDesignation && isProductType && contentItemId && nodeId && isPTKey

        // show standard editor, for regular text or from OM/SMM content
        if ((!isKeyedText && !hasDesignationMode) || isContentTeamFromStudioPage || !shouldLockKey) {
            return editorComponent
        }

        if (!isKeyedText && hasDesignationMode) {
            return (
                <>
                    <KeyedText
                        keyedText={textPrefix}
                        classes={classes}
                        data-testid="keyed-text-key"
                    />
                    <DesignationField
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        contentItemId={contentItemId}
                        editable={true}
                        inactive={false}
                        showPrefix={false}
                        showSuffix={false}
                    />
                </>
            )
        }

        // show label separately from editable value, for keyed text
        return (
            <>
                <KeyedText
                    keyedText={previewFeatures.hasDrawingDesignation && isPTKey ? textPrefix : keyedText}
                    classes={classes}
                    data-testid="keyed-text-key"
                    isPTKey={isPTKey}
                />
                {previewFeatures.hasDrawingDesignation &&
                    isPTKey &&
                    isProductType &&
                    contentItemId &&
                    nodeId && (
                        <>
                            <DesignationField
                                keyedText={keyedText}
                                keyedTextValue={keyedTextValue}
                                contentItemId={contentItemId}
                                editable={true}
                                inactive={false}
                                showPrefix={false}
                                showSuffix={false}
                            />
                            {':'}
                        </>
                    )}
                {keyedTextValue && editorComponent}
            </>
        )
    }
)
